import * as moment from 'moment';
import axios from 'axios';
import Request from './request';
import { uploadVideoDirect } from './directVideoUpload';
import Notifications from './notifications';
import StripeService from './stripeService';
import i18n from './i18n';

const snakeToCamel = str =>
  str.replace(/([-_][a-z])/g, group =>
    group.toUpperCase().replace('-', '').replace('_', ''),
  );

const SUBSCRIPTION_TYPE = {
  FREE: 'free',
  TRIAL: 'trial',
  STANDARD: 'normal',
};
const SUBSCRIPTION_TYPES = Object.values(SUBSCRIPTION_TYPE);

const PROFILE_BLOCK = {
  INFO: 'info',
  COMPANY: 'company',
  CAREER: 'career',
  EXPERIENCE: 'experience',
  HOBBIES: 'hobbies',
  SOFT_SKILLS: 'softSkills',
  HARD_SKILLS: 'hardSkills',
  LANGUAGES: 'languages',
  CONTACTS: 'contacts',
  MOODS: 'moods',
  MEDIA: 'media',
  SOCIAL_MEDIA: 'socialMedia',
  LEGAL_FINANCES: 'legalFinances',
  AVAILABILITY: 'availability',
  DOCUMENTS: 'documents',
  FORMS: 'forms',
};

const PROFILE_BLOCKS = Object.values(PROFILE_BLOCK);

const PROFILE_TAB = {
  ...PROFILE_BLOCK,
  PAYMENT: 'payment',
  SETTINGS: 'settings',
  CONNECTORS: 'connectors',
  PERFORMANCE: 'performance',
  POSTER: 'poster',
  AVAILABILITY: 'availability',
  SIGN_BY_ME: 'signedByMe',
  DOCUMENTS: 'documents',
  FORMS: 'forms',
  FOLLOWING: 'following',
};

const PROFILE_TABS = Object.values(PROFILE_TAB);

const CHALLENGE_TAB = {
  TIMELINE: 'timeline',
  STATS: 'stats',
  INFO: 'info',
  LEADERBOARD: 'leaderboard',
  LEADS: 'leads',
  RECOMMENDATIONS: 'recommendations',
  COURSE: 'course',
  PERFORMANCE: 'performance',
};

const CHALLENGE_TABS = Object.values(CHALLENGE_TAB);

const INBOX_MSG_TYPE = {
  INFO: 'info',
  RATING: 'rating',
  LIKE: 'like',
  COMMENT: 'comment',
  GAME_START: 'game_start',
  GAME_END: 'game_end',
  TROPHY: 'trophy',
  NEWS: 'news',
  MAGAZINE: 'magazine',
  SURVEY: 'survey',
  CUSTOM: 'custom',
  CHAT_JOINED: 'chat_joined',
  HIGHLIGHT_SURVEY_QUESTION: 'highlight_survey_question',
  EVENT: 'event',
  NEW_TIP: 'new_tip',
  PROFILING: 'profiling',
  COURSE_PENDING: 'course_pending',
  COURSES_NOT_TAKEN: 'courses_not_taken',
  HAPPY_BIRTHDAY: 'happy_birthday',
  UNLOCK_REWARD: 'unlock_reward',
  CHARITY_REPORT: 'charity_report',
  VIDEO: 'video',
  PDF: 'pdf',
  PODCAST: 'podcast',
  CALENDAR_EVENT: 'calendar_event',
  FIRST_COURSE: 'first_course',
  SURVEY_REWARDS: 'survey_rewards',
  COURSE_REWARDS: 'course_rewards',
  USER_INVITED_CREATED: 'user_invited_created',
  ACCESS_SESSION: 'access_session',
  DRIVE: 'drive',
  WALLET_WITHDRAW_APPROVED: 'wallet_withdraw_approved',
  OPPORTUNITY_NOTE: 'opportunity_note',
  OPPORTUNITY_STATUS: 'opportunity_status',
  OPPORTUNITY_DOCUMENT: 'opportunity_document',
  REFERRAL_ORDER: 'referral_order',
  REFERRAL_FEATURED_PRODUCT_ORDER: 'referral_featured_product_order',
  REFERRAL_LEVEL_UPDATED: 'referral_level_updated',
  REFERRAL_FORECAST_LEVEL_UPDATED: 'referral_forecast_level_updated',
  REFERRAL_SUBSCRIPTION_CANCELED: 'referral_subscription_canceled',
  REFERRAL_SUBSCRIPTION_FAILED_PAYMENT: 'referral_subscription_failed_payment',
  FINAPP_ACADEMY_REQUEST_ACCESS: 'finapp_academy_request_access',
};

const INBOX_MSG_TYPES = Object.values(INBOX_MSG_TYPE);

const INBOX_FILTER = {
  CHALLENGES: 'challenges',
  NEWS: 'info',
  FEED: 'news',
  MEDIA: 'media',
  SURVEYS: 'surveys',
};

const INBOX_FILTERS = Object.values(INBOX_FILTER);

const TROPHY_CONFIG = {
  scores: {
    translationKey: 'trophy_scores',
    imageURL: '/images/trophies/trophy_scores.png',
  },
  points: {
    translationKey: 'trophy_points',
    imageURL: '/images/trophies/trophy_points.png',
  },
  winner: {
    translationKey: 'trophy_winner',
    imageURL: '/images/trophies/trophy_winner.png',
  },
  stimulus: {
    translationKey: 'trophy_stimulus',
    imageURL: '/images/trophies/trophy_stimulus.png',
  },
  topplayer: {
    translationKey: 'trophy_top_player',
    imageURL: '/images/trophies/trophy_topplayer.png',
  },
  'e-learning_1': {
    translationKey: 'trophy_level_1',
    imageURL: '/images/trophies/trophy_level_1.svg',
  },
  'e-learning_2': {
    translationKey: 'trophy_level_2',
    imageURL: '/images/trophies/trophy_level_2.svg',
  },
  'e-learning_3': {
    translationKey: 'trophy_level_3',
    imageURL: '/images/trophies/trophy_level_3.svg',
  },
  'e-learning_4': {
    translationKey: 'trophy_level_4',
    imageURL: '/images/trophies/trophy_level_4.svg',
  },
  custom: {
    translationKey: 'trophy_custom',
    imageURL: '/images/trophies/trophy_custom.svg',
  },
};

const STEPS_CONFIG = {
  profile_info: {
    translationKey: 'profile_info',
    imageURL: '/images/journey/step-types/profile-info.svg',
  },
  profile_company: {
    translationKey: 'profile_company',
    imageURL: '/images/journey/step-types/profile-company.svg',
  },
  profile_career: {
    translationKey: 'profile_career',
    imageURL: '/images/journey/step-types/profile-career.svg',
  },
  profile_contacts: {
    translationKey: 'profile_contacts',
    imageURL: '/images/journey/step-types/profile-contacts.svg',
  },
  profile_experience: {
    translationKey: 'profile_experience',
    imageURL: '/images/journey/step-types/profile-experience.svg',
  },
  profile_hard_skills: {
    translationKey: 'profile_hardSkills',
    imageURL: '/images/journey/step-types/profile-hard-skills.svg',
  },
  profile_hobbies: {
    translationKey: 'profile_hobbies',
    imageURL: '/images/journey/step-types/profile-hobbies.svg',
  },
  profile_languages: {
    translationKey: 'profile_language',
    imageURL: '/images/journey/step-types/profile-language.svg',
  },
  profile_legal_finances: {
    translationKey: 'profile_legal',
    imageURL: '/images/journey/step-types/profile-legal.svg',
  },
  profile_media: {
    translationKey: 'profile_media',
    imageURL: '/images/journey/step-types/profile-media.svg',
  },
  profile_moods: {
    translationKey: 'profile_moods',
    imageURL: '/images/journey/step-types/profile-moods.svg',
  },
  profile_social_media: {
    translationKey: 'profile_social_media',
    imageURL: '/images/journey/step-types/profile-social.svg',
  },
  profile_soft_skills: {
    translationKey: 'profile_soft_skills',
    imageURL: '/images/journey/step-types/profile-soft-skills.svg',
  },
  survey: {
    translationKey: 'profile_survey',
    imageURL: '/images/journey/step-types/profile-survey.svg',
  },
  profile_trophy: {
    translationKey: 'profile_trophy',
    imageURL: '/images/journey/step-types/profile-trophy.svg',
  },
  task: {
    translationKey: 'task',
    imageURL: '/images/journey/step-types/task.svg',
  },
  trophy: {
    translationKey: 'trophy',
    imageURL: '/images/journey/step-types/trophy.svg',
  },
  course: {
    translationKey: 'course',
    imageURL: '/images/journey/step-types/course.svg',
  },
  game: {
    translationKey: 'challenge',
    imageURL: '/images/journey/step-types/challenge.svg',
  },
  session: {
    translationKey: 'session',
    imageURL: '/images/journey/step-types/session.svg',
  },
  signature: {
    translationKey: 'signature',
    imageURL: '/images/journey/step-types/signature.svg',
  },
  market_product_purchase: {
    translationKey: 'purchase',
    imageURL: '/images/journey/step-types/purchase.svg',
  },
  fileUpload: {
    translationKey: 'fileUpload',
    imageURL: '/images/journey/step-types/fileUpload.svg',
  },
  document: {
    translationKey: 'fileUpload',
    imageURL: '/images/journey/step-types/fileUpload.svg',
  },
  fill_and_sign: {
    translationKey: 'fill_and_sign',
    imageURL: '/images/journey/step-types/fill_and_sign.svg',
  },
};

const GAME_TYPE = {
  PRODUCTIVITY: 'productivity',
  CROSS_SELLING: 'cross-selling',
  PROFILING: 'profiling',
  E_LEARNING: 'e-learning',
  CANDIDATES: 'candidates',
};

const GAME_TYPES = Object.values(GAME_TYPE);

const GAME_STATUS = {
  UNSTARTED: 'unstarted',
  ON_GOING: 'ongoing',
  FINISHED: 'finished',
  PERMANENT: 'permanent',
  NONE: '',
};

const GAME_STATUSES = Object.values(GAME_STATUS);

const GAME_EVENT_TYPE = {
  INFO: 'info',
  RANKING_POSITION: 'raking_position',
  GAME_START: 'game_start',
  GOAL_80: 'goal_80',
  GOAL_90: 'goal_90',
  GOAL_100: 'goal_100',
  GAME_WIN: 'game_win',
  TROPHY: 'trophy',
  LEAD_CREATED: 'lead_created',
  LEAD_CHANGED_OWNER: 'lead_changed_owner',
  LEAD_ATTACH_OWNER: 'lead_attach_owner',
  LEAD_NO_OWNER: 'lead_no_owner',
  LEAD_CHANGED: 'lead_changed',
  LEAD_WON: 'lead_won',
  LEAD_LOST: 'lead_lost',
};

const GAME_EVENT_TYPES = Object.values(GAME_EVENT_TYPE);

const LEAD_CONDITION = {
  LEADS: 'leads',
  NEED_DISCOVERED: 'need_discovered',
  PROPOSAL: 'proposal',
};

const LEAD_CONDITIONS = Object.values(LEAD_CONDITION);

const LEAD_STATUS = {
  NO_OWNER: 'no_owner',
  WAITING_OWNER: 'waiting_owner',
  IN_PROGRESS: 'in-progress',
  LOST: 'lost',
  WON: 'won',
};

const LEAD_STATUSES = Object.values(LEAD_STATUS);

const LEAD_DETAILS_TAB = {
  TIMELINE: 'timeline',
  INFO: 'info',
};

const LEAD_DETAILS_TABS = Object.values(LEAD_DETAILS_TAB);

const COURSE_STATUS = {
  UN_STARTED: 'un_started',
  FINISHED: 'finished',
  IN_PROGRESS: 'in_progress',
  WAITING: 'waiting',
  OPENED: 'opened',
};

const COURSE_STATUSES = Object.values(COURSE_STATUS);

const PURCHASE_STATUS = {
  FREE: 'free',
  PAID: 'paid',
  UNPAID: 'unpaid',
};

const isEnrolled = item =>
  item.purchaseStatus === PURCHASE_STATUS.PAID ||
  item.status === COURSE_STATUS.IN_PROGRESS ||
  item.status === COURSE_STATUS.FINISHED;

const isEnrolledOrFree = (loggedUser, item) => {
  if (item.isContentAccessible === false) {
    return false;
  }
  const hasPromotion = !!item.userPromotionPrice;
  const isFreeWithPromotion =
    !item.userPromotionPrice || !item.userPromotionPrice.price;
  const isFreeWithoutPromotion = !item.userPrice || !item.userPrice.price;
  const isFree = hasPromotion ? isFreeWithPromotion : isFreeWithoutPromotion;
  if (loggedUser) {
    return isEnrolled(item) || isFree;
  }
  return false;
};

const isItemFree = item => {
  const hasPromotion = !!item.userPromotionPrice;
  const isFreeWithPromotion =
    !item.userPromotionPrice || !item.userPromotionPrice.price;
  const isFreeWithoutPromotion = !item.userPrice || !item.userPrice.price;
  return hasPromotion ? isFreeWithPromotion : isFreeWithoutPromotion;
};

// Journeys only (BRAVON-6317): unlike courses (isEnrolledOrFree), a free
// journey is not open by default — the user must explicitly start it. Started
// free journeys report status in_progress / isStarted from the backend.
const isJourneyUnlocked = (loggedUser, journey) => {
  if (journey.isContentAccessible === false) {
    return false;
  }
  if (!loggedUser) {
    return false;
  }
  return isEnrolled(journey) || journey.isStarted === true;
};

// BRAVON-6462: the item was ordered on a method somebody has to confirm by hand
// — company invoice, bank transfer, cheque, crypto — and nobody has signed the
// money off yet. `purchaseStatus` stays `unpaid` the whole time, which is why
// the detail pages used to offer "Enroll" again to a buyer who had already
// bought. The API supplies the flag; enrolment still wins, and an anonymous
// visitor has no order to be waiting on.
const isAwaitingPaymentConfirmation = (loggedUser, item) =>
  !!loggedUser &&
  item.awaitingPaymentConfirmation === true &&
  !isEnrolled(item);

const VOUCHER_STATUS = {
  EXPIRED: 'expired',
  USED: 'used',
  REVOKED: 'revoked',
  ACTIVE: 'active',
};

const VOUCHER_STATUSES = Object.values(VOUCHER_STATUS);

const COURSE_PROVIDER = {
  BRAVON: 'bravon',
  SCORM: 'scorm',
};

const COURSE_PROVIDERS = Object.values(COURSE_PROVIDER);

const MODULE_TYPE = {
  LESSONS: 'lessons',
  TROPHY: 'trophy',
  CERTIFICATE: 'certificate',
  INFO: 'info',
};

const MODULE_TYPES = Object.values(MODULE_TYPE);

const MODULE_STATUS = {
  DONE: 'done',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
  IN_PROGRESS: 'in_progress',
};

const MODULE_STATUSES = Object.values(MODULE_STATUS);

const LESSON_TYPE = {
  VIDEO: 'video',
  PDF: 'pdf',
  IMAGES: 'images',
  QUIZ: 'quiz',
  YOUTUBE: 'youtube',
  WEBINAR: 'webinar',
  ZOOM: 'zoom',
  SURVEY: 'survey',
};

const LESSON_TYPES = Object.values(LESSON_TYPE);

const LESSON_CONTENT_TYPE = {
  VIDEO: 'video',
  PDF: 'pdf',
  IMAGE: 'image',
  MOTION: 'motion',
  WHITEBOARD: 'whiteboard',
  INTERVIEW: 'interview',
};

const LESSON_CONTENT_TYPES = Object.values(LESSON_CONTENT_TYPE);

const LESSON_STATUS = {
  DONE: 'done',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
};

const LESSON_STATUSES = Object.values(LESSON_STATUS);

const QUESTION_STATUS = {
  CORRECT: 'correct',
  INCORRECT: 'incorrect',
  NOT_ANSWERED: 'not_answered',
};

const QUESTION_STATUSES = Object.values(QUESTION_STATUS);

const JOURNEY_STATUS = {
  UN_STARTED: 'un_started',
  IN_PROGRESS: 'in_progress',
  FINISHED: 'finished',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

const JOURNEY_STATUSES = Object.values(JOURNEY_STATUS);

const JOURNEY_STEP_STATUS = {
  UNLOCKED: 'unlocked',
  LOCKED: 'locked',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  WAITING_APPROVAL: 'waiting_approval',
  REJECTED: 'rejected',
};

const JOURNEY_STEP_STATUSES = Object.values(JOURNEY_STEP_STATUS);

const JOURNEY_TASK_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

const JOURNEY_TASK_STATUSES = Object.values(JOURNEY_TASK_STATUS);

const JOURNEY_STEP_TYPE = {
  TASK: 'task',
  GAME: 'game',
  COURSE: 'course',
  SURVEY: 'survey',
  TROPHY: 'trophy',
  CERTIFICATE: 'certificate',
  PROFILE_INFO: 'profile_info',
  PROFILE_COMPANY: 'profile_company',
  PROFILE_CAREER: 'profile_career',
  PROFILE_EXPERIENCE: 'profile_experience',
  PROFILE_HOBBIES: 'profile_hobbies',
  PROFILE_SOFT_SKILLS: 'profile_soft_skills',
  PROFILE_HARD_SKILLS: 'profile_hard_skills',
  PROFILE_LANGUAGES: 'profile_languages',
  PROFILE_CONTACTS: 'profile_contacts',
  PROFILE_MOODS: 'profile_moods',
  PROFILE_MEDIA: 'profile_media',
  PROFILE_SOCIAL_MEDIA: 'profile_social_media',
  PROFILE_LEGAL_FINANCES: 'profile_legal_finances',
  PROFILE_DOCUMENTS: 'profile_documents',
  PROFILE_FORMS: 'profile_forms',
  SIGNATURE: 'signature',
  MARKET_PRODUCT_PURCHASE: 'market_product_purchase',
  SESSION: 'session',
  DOCUMENT: 'document',
  FILL_AND_SIGN: 'fill_and_sign',
};

const JOURNEY_STEP_TYPES = Object.values(JOURNEY_STEP_TYPE);

// TODO: this should be done server side on JourneyManager,
// in a deserialization function like other entities have, we just need
// a good way of passing the available services into the manager, probably the constructor
const normalizeJourney = (services, journey) => {
  const disabledBlocks =
    services && services.me && services.me.config && services.me.config.disabled
      ? services.me.config.disabled
      : [];
  const steps = (journey.steps || [])
    .filter(step => {
      if (step.type === JOURNEY_STEP_TYPE.GAME) {
        return !(
          services &&
          services.perform &&
          services.perform.enabled === false
        );
      }
      if (step.type === JOURNEY_STEP_TYPE.SURVEY) {
        return !(
          services &&
          services.survey &&
          services.survey.enabled === false
        );
      }
      if (step.type.startsWith('profile_')) {
        const blockName = snakeToCamel(step.type.slice(8));
        return !disabledBlocks.includes(blockName);
      }
      return true;
    })
    .map(step => {
      let { status } = step;

      if (
        step.type !== JOURNEY_STEP_TYPE.TROPHY &&
        step.status === JOURNEY_STEP_STATUS.LOCKED &&
        !journey.isSequential
      ) {
        status = JOURNEY_STEP_STATUS.UNLOCKED;
      }

      if (
        step.type === JOURNEY_STEP_TYPE.TASK ||
        step.type === JOURNEY_STEP_TYPE.SIGNATURE ||
        step.type === JOURNEY_STEP_TYPE.DOCUMENT
      ) {
        switch (step.taskStatus) {
          case JOURNEY_TASK_STATUS.APPROVED:
            status = JOURNEY_STEP_STATUS.DONE;
            break;
          case JOURNEY_TASK_STATUS.PENDING:
            status = JOURNEY_STEP_STATUS.WAITING_APPROVAL;
            break;
          case JOURNEY_TASK_STATUS.REJECTED:
            status = JOURNEY_STEP_STATUS.REJECTED;
            break;
          default:
        }
      }

      if (step.type === JOURNEY_STEP_TYPE.CERTIFICATE) {
        if (step.certificate) {
          status = JOURNEY_STEP_STATUS.UNLOCKED;
        } else {
          status = JOURNEY_STEP_STATUS.LOCKED;
        }
      }

      return { ...step, status };
    });
  return { ...journey, steps };
};

const SURVEY_TYPE = {
  SURVEY: 'survey',
  EVENT: 'event',
};

const SURVEY_TYPES = Object.values(SURVEY_TYPE);

const SURVEY_QUESTION_TYPE = {
  CHOICE: 'choice',
  OPTIONS: 'options',
  MULTIPLE_ANSWERS: 'multiple_answers',
  EVALUATION: 'evaluation',
  OPINION: 'opinion',
};

const SURVEY_QUESTION_TYPES = Object.values(SURVEY_QUESTION_TYPE);

const WORK_TIME = {
  H7_16: '7h-16h',
  H8_17: '8h-17h',
  H9_18: '9h-18h',
  H10_19: '10-19h',
  OTHER: 'other',
};

const WORK_TIMES = Object.values(WORK_TIME);

const VEHICLE = {
  ON_FOOT: 'on_foot',
  BICYCLE: 'bicycle',
  MOTORCYCLE: 'motorcycle',
  CAR: 'car',
  TRUCK: 'truck',
  BUS: 'bus',
  TAXI: 'taxi',
  SUBWAY: 'subway',
  TRAIN: 'train',
  SHIP: 'ship',
  PLANE: 'plane',
};

const VEHICLES = Object.values(VEHICLE);

const IN_APP_PRODUCT_TYPE = {
  POSTER: 'poster',
  CERTIFICATE: 'certificate',
  DIGITAL_CERTIFICATE: 'digital_certificate',
};

const IN_APP_PRODUCT_TYPES = Object.values(IN_APP_PRODUCT_TYPE);

const TOPIC_CHAPTER = {
  WALL: 'wall',
  PERFORM: 'perform',
  ACADEMY: 'academy',
  WHOSWHO: 'whoswho',
};

const TOPIC_CHAPTERS = Object.values(TOPIC_CHAPTER);

const TOPIC_TYPE = {
  TEXT: 'text',
  BANNER: 'banner',
};

const TOPIC_TYPES = Object.values(TOPIC_TYPE);

const SOCIAL_MEDIA_CONFIG = {
  facebook: { icon: 'socicon-facebook', name: 'Facebook' },
  instagram: { icon: 'socicon-instagram', name: 'Instagram' },
  linkedin: { icon: 'socicon-linkedin', name: 'Linked In' },
  periscope: { icon: 'socicon-periscope', name: 'Periscope' },
  twitter: { icon: 'socicon-twitter', name: 'Twitter' },
  weibo: { icon: 'socicon-weibo', name: 'Weibo' },
  youtube: { icon: 'socicon-youtube', name: 'YouTube' },
};

const SOCIAL_MEDIA_SITE = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  LINKEDIN: 'linkedin',
  PERISCOPE: 'periscope',
  TWITTER: 'twitter',
  WEIBO: 'weibo',
  YOUTUBE: 'youtube',
};

const SOCIAL_MEDIA_SITES = Object.values(SOCIAL_MEDIA_SITE);

const ACADEMY_LIST_ITEM_TYPE = {
  JOURNEY: 'journey',
  COURSE: 'course',
  SESSION: 'session',
};

const ACADEMY_LIST_ITEM_TYPES = Object.values(ACADEMY_LIST_ITEM_TYPE);

const USER_DOC_TYPE = {
  NONE: 'none',
  NIB_VALIDATION: 'nib_validation',
  EMPLOYEE_FORM: 'employee_form',
  ADDITIONAL_IDENTITY: 'addicional_identity',
};

const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PENDING_PAYMENT: 'pending_payment',
  CANCELED: 'canceled',
  FAILED: 'failed',
  OUT_OF_STOCK: 'out_of_stock',
};
const PLAN_STATUS = {
  ACTIVE: 'active',
  FAILED: 'failed',
  FAILED_AND_EXPIRED: 'failed_and_expired',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
  TRIAL: 'trial',
};

const USER_DOC_TYPES = Object.values(USER_DOC_TYPE);

const ORDER_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'pending', text: 'pending', order: 2 },
  { status: 'processing', text: 'processing', order: 3 },
  { status: 'shipped', text: 'shipped', order: 4 },
  { status: 'completed', text: 'completed', order: 5 },
  {
    status: 'processing_error',
    text: 'processing_error',
    order: 6,
  },
  { status: 'out_of_stock', text: 'out_of_stock', order: 7 },
  { status: 'canceled', text: 'canceled', order: 8 },
  { status: 'refunded', text: 'refunded', order: 9 },
  { status: 'failed', text: 'failed', order: 10 },
  { status: 'lost', text: 'lost', order: 11 },
];

const SUBSCRIPTION_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'pending', text: 'pending', order: 2 },
  { status: 'processing', text: 'pending', order: 3 },
  { status: 'processing_error', text: 'processing_error', order: 4 },
  { status: 'active', text: 'active', order: 5 },
  { status: 'completed', text: 'completed', order: 6 },
  { status: 'canceled', text: 'canceled', order: 7 },
  { status: 'refunded', text: 'refunded', order: 8 },
  { status: 'failed', text: 'failed', order: 9 },
  { status: 'out_of_stock', text: 'out_of_stock', order: 10 },
  { status: 'lost', text: 'lost', order: 11 },
];

const PLAN_STATUSES = [
  { status: 'active', text: 'active', order: 1 },
  { status: 'past_due', text: 'past_due', order: 2 },
  { status: 'unpaid', text: 'unpaid', order: 4 },
  { status: 'failed', text: 'failed', order: 5 },
  { status: 'failed_and_expired', text: 'failed_and_expired', order: 6 },
  { status: 'canceled', text: 'canceled', order: 7 },
];

const ACADEMY_ORDER_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'completed', text: 'completed', order: 5 },
  { status: 'canceled', text: 'canceled', order: 6 },
  { status: 'refunded', text: 'refunded', order: 7 },
  { status: 'failed', text: 'failed', order: 8 },
];

const ORDER_HISTORY_TYPES = [
  { type: 'status_change', text: 'order_status_change', order: 1 },
  { type: 'payment_method_change', text: 'payment_method_change', order: 4 },
  { type: 'refund', text: 'order_refund', order: 2 },
  { type: 'rma_add', text: 'order_rma_add', order: 3 },
  { type: 'rma_update', text: 'order_rma_update', order: 10 },
];

const BUYER_TYPE = {
  OWNER: 'owner',
  SELLER: 'seller',
  CLIENTS: 'clients',
};

const BUYER_TYPES = Object.values(BUYER_TYPE);

const PRODUCT_TYPE = {
  PRODUCT: 'product',
  GIFT: 'gift',
  PACK: 'pack',
  DOWNLOADABLE: 'downloadable',
  SERVICES: 'services',
  WORKSHOPS: 'workshops',
  LICENSES: 'licenses',
};

const PRODUCT_TYPES = Object.values(PRODUCT_TYPE);

const SESSION_STATUS = {
  FINISHED: 'finished',
  LEAD: 'lead',
  CLOSED: 'closed',
  ONGOING: 'ongoing',
  PLANNED: 'planned',
  CONFIRMED: 'confirmed',
  UNSTARTED: 'un_started',
  CANCELED: 'canceled',
};

const SESSION_STATUSES = Object.values(SESSION_STATUS);

const SESSION_TYPE = {
  ONLINE: 'online',
  WEBINAR: 'webinar',
  CLASSROOM: 'classroom',
};

const SESSION_TYPES = Object.values(SESSION_TYPE);

const PAYMENT_METHOD = {
  CREDIT_CARD: 'credit_card',
  CREDIT_CARD_SOURCE: 'credit_card_source',
  BANK_TRANSFER: 'bank_transfer_manual',
  BANCONTACT: 'bancontact',
  PAID_BY_COMPANY: 'paid_by_company',
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
  CHEQUE: 'cheque',
  CRYPTO_MANUAL: 'crypto_manual',
  PAYPAL: 'paypal',
  KLARNA: 'klarna',
  // Paid on an external Stripe account via a Payment Link (BRAVON-6316)
  EXTERNAL_LINK: 'external_link',
};

const PAYMENT_METHODS = Object.values(PAYMENT_METHOD);

const SESSION_MODE = {
  PRIVATE: 'private',
  PUBLIC: 'public',
};

const ORDER_STATUS_ENUM = {
  PENDING: 'pending',
  PENDING_PAYMENT: 'pending_payment',
  PROCESSING: 'processing',
  PROCESSING_ERROR: 'processing_error',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  REFUNDED: 'refunded',
  FAILED: 'failed',
  LOST: 'lost',
};

// Methods settled outside the checkout: the order is created and stays
// `pending_payment` until someone confirms the money arrived. We cannot read the
// order status right after a purchase — the API reports `pending_payment` for
// every freshly created order, card payments included — so the method is what
// tells the two apart.
const MANUAL_PAYMENT_METHODS = [
  PAYMENT_METHOD.PAID_BY_COMPANY,
  PAYMENT_METHOD.BANK_TRANSFER,
  PAYMENT_METHOD.CHEQUE,
  PAYMENT_METHOD.CRYPTO_MANUAL,
];

// `total` guards free items: a zero-total order is reported as a manual method
// but is settled on the spot, so it must not be treated as awaiting payment.
const isPaymentPending = (paymentMethod, total) =>
  total > 0 && MANUAL_PAYMENT_METHODS.includes(paymentMethod);

const numberWithSpaces = x =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const displayNumber = number => {
  if (number === '') {
    return '';
  }

  try {
    let value = numberWithSpaces(Number(number).toFixed(2));

    const decimalPlaces = value.split('.');
    if (decimalPlaces[1]) {
      value = decimalPlaces[1].length < 2 ? `${value}0` : value;
    } else {
      value = `${value}.00`;
    }

    let lang = 'en';
    if (document) {
      const data = document.querySelector('script[data-lang]');
      if (data) {
        lang = data.getAttribute('data-lang');
      }
    }
    if (lang && lang.startsWith('fr')) {
      value = value.replace('.', ',');
    }

    return value;
  } catch (err) {
    return '';
  }
};

const displayMoney = (number, symbol = '€') => {
  try {
    const numberValue = Number(number) / 100;
    const value = displayNumber(numberValue);
    return value ? `${symbol}${value}` : value;
  } catch (err) {
    return '';
  }
};

const displayUserName = (user, showUserName = false) => {
  if (!user) {
    return '';
  }
  if (!showUserName && user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.username;
};

const displayRegionalAddress = address => {
  if (!address) {
    return '';
  }
  const state = address.state ? ` ${address.state}` : '';
  return `${address.zipCode}${address.state ? ` ${state}` : ''} ${
    address.city
  }`;
};

// Add the optional fields in billing address
const handleBillingAddressInOrder = (user, address) => {
  const billingAddress = address;
  // Add the optionals fields to billing address
  if (user?.address?.firstName) {
    billingAddress.firstName = user.address?.firstName;
  } else if (user?.firstName) {
    billingAddress.firstName = user.firstName;
  }
  if (user?.address?.lastName) {
    billingAddress.lastName = user.address?.lastName;
  } else if (user?.lastName) {
    billingAddress.lastName = user.lastName;
  }
  if (user?.address?.email) {
    billingAddress.email = user.address?.email;
  } else if (user?.email) {
    billingAddress.email = user.email;
  }
  if (user?.address?.phone) {
    billingAddress.phone = user.address?.phone;
  } else if (user?.phone) {
    billingAddress.phone = user.phone;
  }
  return billingAddress;
};
// Check if the optional fields don't exist in shipping address
const handleShippingAddressInOrder = (user, shippingAddress) => {
  const shippingAddressUpdated = shippingAddress;
  if (shippingAddress && user) {
    if (!shippingAddress.firstName) {
      shippingAddressUpdated.firstName = user?.firstName;
    }
    if (!shippingAddress.lastName) {
      shippingAddressUpdated.lastName = user?.lastName;
    }
    if (!shippingAddress.email) {
      shippingAddressUpdated.email = user?.email;
    }
    if (!shippingAddress.phone) {
      shippingAddressUpdated.phone = user?.phone;
    }
  }
  return shippingAddressUpdated;
};

const displayTrophyName = (t, trophy) => {
  const finalTrophy = trophy || {};
  let name;
  ({ name } = finalTrophy);
  if (finalTrophy.type === 'custom' && finalTrophy.trophyCustom) {
    ({ name } = finalTrophy.trophyCustom);
  } else if (trophy.type && TROPHY_CONFIG[finalTrophy.type]) {
    name = t(TROPHY_CONFIG[finalTrophy.type].translationKey);
  } else if (trophy.level && TROPHY_CONFIG[`e-learning_${trophy.level}`]) {
    name = t(TROPHY_CONFIG[`e-learning_${trophy.level}`].translationKey);
  }
  return name;
};

const debounce = (func, delay) => {
  let inDebounce;
  return function debounced(...args) {
    const context = this;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function throttled(...args) {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const compose = (fn, ...rest) =>
  rest.length === 0 ? fn : (...args) => fn(compose(...rest)(...args));

const displayCardNumber = last4 => `**** **** **** ${last4}`;

const colors = colorParam => {
  const color = colorParam === 'primary' ? 'dynamic' : colorParam;

  const fallback = {
    dynamic: '#abb7b6',
    inverse: '#ebedf2',
    secondary: '#ebedf2',
    metal: '#c4c5d6',
    light: '#ffffff',
    accent: '#00c5dc',
    success: '#34bfa3',
    info: '#36a3f7',
    warning: '#ffb822',
    danger: '#f4516c',
    focus: '#9816f4',
  };
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    const cssVal = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(`--${color}`);
    return cssVal || fallback[color];
  }
  return fallback[color];
};

const addAlphaToHex = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const hexToHSL = hex => {
  // Convert hex to RGB first
  let r = 0;
  let g = 0;
  let b = 0;

  if (hex.length === 4) {
    r = `0x${hex[1]}${hex[1]}`;
    g = `0x${hex[2]}${hex[2]}`;
    b = `0x${hex[3]}${hex[3]}`;
  } else if (hex.length === 7) {
    r = `0x${hex[1]}${hex[2]}`;
    g = `0x${hex[3]}${hex[4]}`;
    b = `0x${hex[5]}${hex[6]}`;
  }

  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s;
  let l;

  if (delta === 0) {
    h = 0;
  } else if (cmax === r) {
    h = ((g - b) / delta) % 6;
  } else if (cmax === g) {
    h = (b - r) / delta + 2;
  } else {
    h = (r - g) / delta + 4;
  }

  h = Math.round(h * 60);

  if (h < 0) {
    h += 360;
  }

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return { h, s, l };
};

const hslToHex = (pH, pS, pl) => {
  const h = pH / 360;
  const s = pS / 100;
  const l = pl / 100;
  let r;
  let g;
  let b;

  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const changeLightness = (amount, color) => {
  if (!color) {
    return '';
  }
  const { h, s, l } = hexToHSL(color);
  return hslToHex(h, s, l * amount);
};

/**
 * @param t translation function
 * @param _csrf csrf token
 * @param imageState `{blob, fileName}` picked in the form
 * @param {boolean} isVideo
 * @param {?string} [uploadContext] BRAVON-6332 Phase 2b — the private-storage
 *   family this upload belongs to (see `VIDEO_UPLOAD_CONTEXT`). Sent as a form
 *   field so the endpoint MAY put the object on the private bucket; an
 *   endpoint that does not know the field ignores it, and a caller that passes
 *   nothing posts the exact same body it posted before.
 * @returns {Promise<*>} the upload response payload
 */
const handleImageUpload = async (
  t,
  _csrf,
  imageState,
  isVideo,
  uploadContext,
) => {
  if (!imageState || !imageState.blob) {
    return null;
  }
  // BRAVON-6332 Phase 2b — a video that named a private family goes browser ->
  // S3 first, whatever its size: the portal's own `/upload/video` writes
  // `public-read` onto the PUBLIC bucket and never reaches api_games, so it
  // could not put a small clip anywhere private. `uploadVideoDirect` answers
  // NULL whenever the direct path is unavailable or refused, and the legacy
  // POST below then runs exactly as it always did.
  if (isVideo && uploadContext) {
    const direct = await uploadVideoDirect(
      _csrf,
      imageState.blob,
      uploadContext,
    );
    if (direct) {
      return direct;
    }
  }
  return new Promise(resolve => {
    const { fileName, blob } = imageState;
    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('upload_file', true);
    if (uploadContext) {
      formData.append('context', uploadContext);
    }
    const url = isVideo ? '/upload/video' : '/upload/image';

    new Request(_csrf)
      .formMulti('POST', url, formData)
      .done(result => resolve(result.data))
      .fail(err => {
        const error = isVideo ? 'error_videoUpload' : 'error_imageUpload';
        Notifications.showErrorFromRequest(err, error);
      });
  });
};

const partitionArray = (array, partitionSize) =>
  Array(Math.ceil(array.length / partitionSize))
    .fill(0)
    .map((_, index) => index * partitionSize)
    .map(begin => array.slice(begin, begin + partitionSize));

const getSizeFromString = sizeString => {
  const SIZE_REGEX = /^([0-9]+\.?[0-9]+)px$/;
  const matches = sizeString.match(SIZE_REGEX);
  if (!matches || matches.length < 2) {
    return 0;
  }
  return Number(matches[1]);
};

const getWidthForElement = element => {
  const style = getComputedStyle(element);
  const [width, margin] = ['width', 'margin-right'].map(attr =>
    getSizeFromString(style[attr]),
  );
  return width + margin * 2;
};

const formatDateTime = (time, format = "DD MMM, 'YY - HH:mm") =>
  moment.utc(time).format(format);

const formatDate = (time, format = "DD MMM, 'YY") =>
  moment.utc(time).format(format);

const dateWithoutTimeZone = date => {
  let dateWithoutZone = date;
  if (!date.isUtc()) {
    const zone = date.utcOffset();
    dateWithoutZone = date.add(zone, 'minutes');
    dateWithoutZone = dateWithoutZone.utcOffset(0);
  }
  return dateWithoutZone;
};

const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const linkify = text => {
  const urlRegex =
    /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
  return text.replace(urlRegex, url => `<a href="${url}">${url}</a>`);
};
const findCurrencySymbol = (code, currencies) => {
  if (!code || !Array.isArray(currencies)) return '';
  return currencies.find(c => c.code === code.toUpperCase())?.symbol ?? '';
};
const formatPrice = (centsValue, symbol) =>
  `${(Math.round(centsValue) / 100).toFixed(2)}${symbol}`;
const getNavigatorLanguage = () => {
  if (typeof window !== 'undefined') {
    if (navigator) {
      return navigator.language;
    }
  }
  return undefined;
};
const newFormatPrice = (number, isCents, code, isFloor) => {
  let value = Number(number);
  if (Number.isNaN(value)) return '';

  if (isCents) {
    value = isFloor ? Math.floor(number) / 100 : Math.round(number) / 100;
  }

  let strValue = `${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: code ? code.toUpperCase() : 'EUR',
    style: 'currency',
  })}`;
  strValue = strValue.replace(/,/g, ' ');
  return strValue;
};

const formatNumbers = (number, isCents = false, hasDecimalPlaces = true) => {
  if (typeof number !== 'number') return '-';
  let value = number;
  if (isCents) {
    value = Math.round(number / 100);
  }
  let options = {};
  if (hasDecimalPlaces) {
    options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }

  let strValue = value.toLocaleString('en-US', options);
  strValue = strValue.replace(/,/g, ' ');
  return strValue;
};

const formatNumbersWithZero = (
  number,
  isCents = false,
  hasDecimalPlaces = true,
) => {
  if (number === 0) return number;
  return formatNumbers(number, isCents, hasDecimalPlaces);
};

const formatVA = val => {
  if (val == null) return '-';
  const result = formatNumbersWithZero(val, false, true);
  if (typeof result === 'string') return result.replace(/\.00$/, '');
  return result;
};

const getSeparator = separatorType => {
  const numberWithGroupAndDecimalSeparator = 10000.1;
  const separatorsArray = Intl.NumberFormat('en-US').formatToParts(
    numberWithGroupAndDecimalSeparator,
  );

  // Adapted it to our format decimal = "." group (thousands) = " "
  const ourSeparator = separatorsArray.map(s => {
    let separatorItem = s;
    if (s.type === 'group') {
      separatorItem = { type: s.type, value: ' ' };
    }
    return separatorItem;
  });

  const separator = ourSeparator.find(
    part => part.type === separatorType,
  ).value;
  return separator;
};
const showProgress = progress => {
  if (progress % 1 > 0) {
    return progress.toFixed(2);
  }
  return progress;
};

const getLink = url => {
  if (url && (url.startsWith('https://') || url.startsWith('http://'))) {
    return url;
  }
  return `https://${url}`;
};

const formatURL = url => {
  if (!url) {
    return '';
  }
  if (url.endsWith('/')) {
    return url;
  }
  return `${url}/`;
};

const handleStripePayment = async (paymentMethod, secret) =>
  StripeService.handlePaymentConfirmation(paymentMethod, secret);

const encodeString = obj => new Buffer(JSON.stringify(obj)).toString('base64');
const decodeString = obj => {
  const buff = new Buffer(obj, 'base64');
  return JSON.parse(buff);
};

// DECODE COOKIES
const parseCookies = request => {
  const list = {};
  const cookieHeader = request && request.headers && request.headers.cookie;
  if (!cookieHeader) return list;

  cookieHeader.split(';').forEach(cookie => {
    // eslint-disable-next-line prefer-const
    let [name, ...rest] = cookie.split('=');
    name = name && name.trim();
    if (!name) return;
    const value = rest.join('=').trim();
    if (!value) return;
    list[name] = decodeURIComponent(value);
  });

  return list;
};

// Responsible to add the values saved in the cookies
const returnParams = (req, previousParams = {}, notforceCountry = null) => {
  const { localCurrency, localCountry, localLanguage } = parseCookies(req);
  const params = previousParams;
  if (localCurrency) {
    params.currency = localCurrency;
  }
  if (localCountry && !notforceCountry) {
    params.country = localCountry;
  }
  // some filters use the param language, so we need to be careful
  if (!req.user && localLanguage) {
    params.language = localLanguage;
  }
  return params;
};

const extractLanguageFromCookies = req => {
  const { localLanguage } = parseCookies(req);
  return localLanguage;
};

const gameStatus = (startDate, endDate) => {
  if (startDate && endDate) {
    const today = moment().valueOf();

    if (today < startDate) {
      return 'unstarted';
    }
    if (today > startDate && today < endDate) {
      return 'ongoing';
    }
    if (today > endDate) {
      return 'finished';
    }
    return '';
  }
  return 'permanent';
};

const getBrowserFullscreenElementProp = () => {
  if (typeof document.fullscreenElement !== 'undefined') {
    return 'fullscreenElement';
  }
  if (typeof document.mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement';
  }
  if (typeof document.msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement';
  }
  if (typeof document.webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement';
  }
  return null;
};
const handleClickFullScreen = id => {
  const fullscreenMode = getBrowserFullscreenElementProp();
  switch (fullscreenMode) {
    case 'fullscreenElement':
      document.getElementById(id).requestFullscreen();
      break;
    case 'mozFullScreenElement' /* Firefox */:
      document.getElementById(id).mozFullScreenElement();
      break;
    case 'msFullscreenElement' /* IE11 */:
      document.getElementById(id).msRequestFullscreen();
      break;
    case 'webkitFullscreenElement' /* Safari */:
      document.getElementById(id).webkitRequestFullscreen();
      break;
    default:
      throw new Error('fullscreenElement is not supported by this browser');
  }
};

const returnAddress = addressParam => {
  let addressComposed = '';
  if (addressParam.address_1) {
    addressComposed += addressParam.address_1;
  }
  if (addressParam.address_2) {
    addressComposed += `, ${addressParam?.address_2}`;
  }
  if (addressParam.zipCode) {
    addressComposed += `, ${addressParam?.zipCode}`;
  }
  if (addressParam.city) {
    addressComposed += `, ${addressParam?.city}`;
  }
  if (addressParam.country) {
    addressComposed += `, ${addressParam?.country}`;
  }
  return addressComposed;
};

// FOR PDF
const handleUploadFile = async (
  _csrf,
  event,
  dataParam = null,
  fileId = null,
) => {
  let data;
  let originalName;
  let size;
  let ext;
  if (!dataParam) {
    const file = event.target.files[0];

    originalName = file.name;
    const { size: sizeParam } = file;
    const nameParts = originalName.split('.');
    const extParam = `.${nameParts[nameParts.length - 1]}`;
    size = sizeParam;
    ext = extParam;
    data = new FormData();
    data.append('file', file);
  } else {
    data = dataParam.formData;
    size = dataParam.size || null;
    ext = dataParam.ext || null;
    originalName = dataParam.originalName || null;
  }

  let endpoint = '/upload/file';
  if (fileId) {
    endpoint += `?id=${fileId}`;
  }

  const { data: response } = await axios.post(endpoint, data, {
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Games',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });
  const newNameParts = response.data.split('/');
  const newName = newNameParts[newNameParts.length - 1];

  return {
    url: response.data,
    name: newName,
    originalName: originalName || newName,
    ext,
    size,
  };
};

const RewardTypes = {
  COINS: 'coins',
  CHARITY_COINS: 'charityCoins',
  COMMISSION: 'commission',
  COMMISSION_VA: 'commissionVA',
  COMMISSION_CURRENCY: 'commissionCurrency',
  COUPONS: 'coupons',
  VOUCHERS: 'vouchers',
  WALLET_BALANCE: 'walletBalance',
};

const returnAcademyContext = (url, journeyContext, sessionContext) => {
  let path = url;
  if (journeyContext && !sessionContext) {
    path += `?journeyContext=${journeyContext}`;
  } else if (sessionContext && !journeyContext) {
    path += `?sessionContext=${sessionContext}`;
  } else if (sessionContext && journeyContext) {
    path += `?sessionContext=${sessionContext}&journeyContext=${journeyContext}`;
  }
  return path;
};

const checkStripeKeys = loggedUser => {
  const hasCustomerIdKey = loggedUser?.stripe?.customerIdPerKey;
  if (!hasCustomerIdKey) {
    return true;
  }
  const stripeKeys = Object.values(loggedUser.stripe.customerIdPerKey).filter(
    s => s,
  );
  return stripeKeys.length === 0;
};

const PROMO_ERRORS = {
  CURRENT_TIME_GREATER: 'currentTimeIsGreater',
  START_DATE_GREATER: 'startDateIsGreater',
  NO_ERRORS: 'noErrors',
};

const checkPromoDate = (isEdit, accessSession) => {
  const currentTime = moment().valueOf();
  const startDateInMS = moment(
    $('#promotionPriceDateStart').val(),
    'DD MMM YYYY - HH:mm',
  ).valueOf();
  const endDateInMS = moment(
    $('#promotionPriceDateEnd').val(),
    'DD MMM YYYY - HH:mm',
  ).valueOf();

  const isEqualToInitialStartDate =
    isEdit && startDateInMS === accessSession?.promotionPriceDateStart;
  const isEqualToInitialEndDate =
    isEdit && endDateInMS === accessSession?.promotionPriceDateEnd;

  if (isEdit && isEqualToInitialStartDate && isEqualToInitialEndDate) {
    return PROMO_ERRORS.NO_ERRORS;
  }
  if (startDateInMS >= endDateInMS) {
    return PROMO_ERRORS.START_DATE_GREATER;
  }
  if (endDateInMS <= currentTime) {
    return PROMO_ERRORS.CURRENT_TIME_GREATER;
  }
  return PROMO_ERRORS.NO_ERRORS;
};

const getDifferenceBetweenDates = (startDate, endDate) => {
  const start = moment(startDate).endOf('millisecond');
  const end = moment(endDate).endOf('millisecond');

  const days = end.diff(start, 'days');
  const hours = end.diff(start, 'hours') - days * 24;

  const minutes = end.diff(start, 'minutes') - days * 24 * 60 - hours * 60;
  const seconds =
    end.diff(start, 'seconds') - days * 24 * 60 - hours * 60 - minutes * 60;

  return { days, hours, minutes, seconds };
};

const composeDate = (startDate, endDate) => {
  const { days, hours, minutes, seconds } = getDifferenceBetweenDates(
    startDate,
    endDate,
  );

  let estimatedTime = '';
  if (days > 0) {
    const daysTxt = days > 1 ? i18n.__('time.days', days) : i18n.__('time.day');
    estimatedTime += daysTxt;
  }
  if (hours > 0) {
    const hoursTxt =
      hours > 1 ? i18n.__('time.hours', hours) : i18n.__('time.hour');
    estimatedTime += days > 0 ? `, ${hoursTxt}` : hoursTxt;
  }
  if (minutes > 0) {
    const minutesTxt =
      minutes > 1 ? i18n.__('time.minutes', minutes) : i18n.__('time.minute');
    estimatedTime += hours > 0 || days > 0 ? `, ${minutesTxt}` : minutesTxt;
  }
  return { estimatedTime, days, hours, minutes, seconds };
};

const promotionEndingMessage = (
  promotionPriceDateEnd,
  customKey = 'product.promotion.days',
) => {
  const currentDate = moment().valueOf();
  if (!promotionPriceDateEnd || promotionPriceDateEnd < currentDate) {
    return '';
  }
  const { days, minutes, seconds } = getDifferenceBetweenDates(
    currentDate,
    promotionPriceDateEnd,
  );
  if (
    days < 0 ||
    minutes < 0 ||
    (days === 0 && minutes === 0 && seconds === 0) ||
    days > 4 ||
    (days === 4 && minutes === 0)
  ) {
    return '';
  }

  if (days < 1 && (minutes > 0 || (minutes === 0 && seconds > 0))) {
    return i18n.__('product.promotion.today');
  }
  const moreThatTheDay = minutes > 0 ? days + 1 : days;
  return i18n.__(customKey, moreThatTheDay);
};

const calculatePromoPercentage = (price, promoPrice) => {
  if (price && promoPrice && price > promoPrice) {
    return Math.round((promoPrice * 100) / price) || null;
  }
  return '';
};

const returnDateMsg = (days, hours, minutes) => {
  const daysStr = i18n.__('time.days', days);
  const hoursStr = i18n.__('time.hours', hours);
  const minutesStr = i18n.__('time.minutes', minutes);
  return `${days > 0 ? `${daysStr}` : ''}${
    days === 0 && hours > 0 ? `, ${hoursStr}` : ''
  }
  ${hours === 0 && minutes > 0 ? `, ${minutesStr}` : ''}`;
};

const renderPromotionMessage = (
  t,
  promotionPriceDateStart,
  promotionPriceDateEnd,
) => {
  const currentDate = moment().utc().valueOf();

  const promoStartDate =
    promotionPriceDateStart && formatDateTime(promotionPriceDateStart);
  const promoEndDate =
    promotionPriceDateEnd && formatDateTime(promotionPriceDateEnd);

  if (
    (!promotionPriceDateStart && !promotionPriceDateEnd) ||
    (!promotionPriceDateStart && currentDate > promotionPriceDateEnd) ||
    (promotionPriceDateStart &&
      promotionPriceDateEnd &&
      promotionPriceDateStart > promotionPriceDateEnd)
  ) {
    return null;
  }
  if (promoStartDate && !promoEndDate) {
    return t('product.variation.promotion.message.1', promoStartDate);
  }
  if (!promoStartDate && promoEndDate) {
    const { days, hours, minutes } = getDifferenceBetweenDates(
      currentDate,
      promotionPriceDateEnd,
    );
    return t(
      'product.variation.promotion.message.2',
      returnDateMsg(days, hours, minutes),
      promoEndDate,
    );
  }
  const { days, hours, minutes } = getDifferenceBetweenDates(
    promotionPriceDateStart,
    promotionPriceDateEnd,
  );
  return i18n.__(
    'product.variation.promotion.message.3',
    returnDateMsg(days, hours, minutes),
    promoStartDate,
  );
};

const FrequencySubscriptionTypes = {
  ONLY_ONCE: 'only_once',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  HALF_YEARLY: 'half_yearly',
  DAILY: 'daily',
};

const FREQUENCIES_TYPES = Object.values(FrequencySubscriptionTypes);

const getMimeType = ext => {
  switch (ext) {
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xlsx':
      return 'application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'csv':
    default:
      return 'text/csv';
  }
};
function downloadFile(fileToDownload) {
  const { url, ext: fileExt, originalName } = fileToDownload;
  const fallbackExt = fileToDownload?.name?.split('.')
    ? fileToDownload?.name?.split('.')[1]
    : ';';
  const ext = fileExt || fallbackExt;
  const name = fileToDownload?.originalName || fileToDownload?.name || '';
  axios({
    // BRAVON-6332 Phase 2b — a private document's stored `url` is the
    // authenticated api_media route, which this cross-origin XHR cannot send a
    // Bearer token to. `resolveDocumentUrl` re-bases it onto THIS portal's
    // session-gated `/file/...` mirror; legacy public urls pass through
    // untouched. See the "Sensitive documents" region below.
    url: resolveDocumentUrl(url),
    params: {},
    method: 'get',
    responseType: 'blob',
  }).then(response => {
    const file = new Blob([response.data], { type: getMimeType(ext) });
    const fileURL = URL.createObjectURL(file);
    const a = document.createElement('A');
    a.href = fileURL;
    a.download = `${name}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
}

const handleInitTab = paymentMethodsParam => {
  if (paymentMethodsParam.includes(PAYMENT_METHOD.CREDIT_CARD)) {
    return PAYMENT_METHOD.CREDIT_CARD;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.BANCONTACT)) {
    return PAYMENT_METHOD.BANCONTACT;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.BANK_TRANSFER)) {
    return PAYMENT_METHOD.BANK_TRANSFER;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.PAID_BY_COMPANY)) {
    return PAYMENT_METHOD.PAID_BY_COMPANY;
  }
  return PAYMENT_METHOD.BANK_TRANSFER;
};

/**
 * Check if the country code ES_AM is allowed for billingAddress countryCode of the user
 * accepts Spain (es), Canary Islands (ic), Ceuta (es-ce), Melilla (es-me)
 * returns true if it is
 * @param countryCode
 * @returns {boolean}
 */
const isBusinessTypeESAMAllowed = countryCode => {
  if (!countryCode) {
    return false;
  }
  return ['es', 'ic', 'es-ce', 'es-me'].includes(countryCode.toLowerCase());
};

/**
 * region Sensitive documents (BRAVON-6335 / Phase 2a)
 *
 * Invoices, credit notes, wallet receipts/invoices, certificate PDFs,
 * opportunity documents, analytics exports and commission reports moved to
 * PRIVATE S3 storage. Their persisted `link`/`url` is therefore no longer a
 * public S3 url but the authenticated api_media route
 * `<mediaBase>/file/:type/:id[/:subId]`, which needs an `Authorization: Bearer`
 * header — a browser opening it in a tab cannot send one.
 *
 * This portal serves the very same path session-gated (`GET /file/...`, see
 * `controllers/index.js` → `HomeHandler.redirectDocumentFile`): it forwards the
 * request to api_media with the session's token and re-emits the 302 to the
 * short-lived presigned url. So fixing a raw `href` is just re-basing the url
 * onto this portal, which is what `resolveDocumentUrl` does.
 *
 * Detection is done on the PATH SHAPE — `/file/<known type>/<24-hex id>` — and
 * deliberately NOT on the api_media host:
 *   - the browser bundle has no access to the configured api_media base, and
 *     this portal is served under many white-label domains, so an origin
 *     comparison would be both unavailable and per-tenant;
 *   - no legacy public S3 key (`invoices/…`, `receipt/…`, `creditNote/…`,
 *     `prints/…`) and no third-party url (Stripe `hosted_invoice_url`, Zoho, …)
 *     has that shape, so a false positive is not reachable in practice.
 *
 * Anything that does not match is returned untouched — that is the transition
 * path for the documents still living on the public bucket until the Stage 5
 * backfill, and for links that were never ours.
 */
const DOCUMENT_FILE_TYPE = {
  INVOICE: 'invoice',
  CREDIT_NOTE: 'credit-note',
  ORDER_INVOICE: 'order-invoice',
  WALLET_RECEIPT: 'wallet-receipt',
  WALLET_INVOICE: 'wallet-invoice',
  CERTIFICATE_PDF: 'certificate-pdf',
  CERTIFICATE_PRINT: 'certificate-print',
  OPPORTUNITY_DOCUMENT: 'opportunity-document',
  USER_REPORT: 'user-report',
  POSTER_PRINT: 'poster-print',
  COMMISSION_REPORT: 'commission-report',
  COMMISSION_CRYPTO_REPORT: 'commission-crypto-report',
  // Phase 2b — lesson/course material, chat media, user documents and form
  // templates. Same shape, same route; only the vocabulary grows.
  COURSE_DOCUMENT: 'course-document',
  LESSON_DOCUMENT: 'lesson-document',
  SESSION_DOCUMENT: 'session-document',
  JOURNEY_STEP_DOCUMENT: 'journey-step-document',
  JOURNEY_STEP_USER_DOCUMENT: 'journey-step-user-document',
  CHAT_FILE: 'chat-file',
  CHAT_IMAGE: 'chat-image',
  CHAT_VIDEO: 'chat-video',
  USER_DOCUMENT: 'user-document',
  FORM_TEMPLATE: 'form-template',
  ORDER_PRODUCT_FORM_TEMPLATE: 'order-product-form-template',
  USER_VIDEO: 'user-video',
  // BRAVON-6677 — the identity-card photographs, one slug per side.
  USER_ID_CARD_FRONT: 'user-id-card-front',
  USER_ID_CARD_BACK: 'user-id-card-back',
  // Stage 5 — the `session-replay` slug has existed on the backend since Stage
  // 2 but no client knew it. It lands here together with the variant sub-id, so
  // all six mirrors change once instead of twice.
  SESSION_REPLAY: 'session-replay',
  // Phase 3, Stage 3 — the inbox (wallEvent) media, reclassified from public
  // urls to authenticated delivery.
  INBOX_FILE: 'inbox-file',
  INBOX_IMAGE: 'inbox-image',
  INBOX_ICON: 'inbox-icon',
  INBOX_MEDIA: 'inbox-media',
  // Phase 3 — the digital product's paid deliverable
  // (marketProductVariation.digitalDocument).
  PRODUCT_DOCUMENT: 'product-document',
  // Phase 3 — the FILLED form-answer PDFs (personal data).
  FORM_ANSWER: 'form-answer',
  ORDER_PRODUCT_FORM_ANSWER: 'order-product-form-answer',
};

const DOCUMENT_FILE_TYPES = Object.values(DOCUMENT_FILE_TYPE);

/**
 * `:subId` is a sub-document ObjectId OR the NAME of a rendition.
 *
 * Chat media is the exception that forced this: an image is three S3 objects
 * hanging off ONE message with no ids of their own, so the rendition is
 * addressed by name (`/file/chat-image/<messageId>/medium`), and the converted
 * chat video's poster frame works the same way. Mirrors
 * `FileLinks.SUB_ID_PATTERN` in core-kit, which is also what the api_media
 * route pattern is built from — the two must accept exactly the same shapes.
 */
const DOCUMENT_FILE_VARIANTS = ['medium', 'thumb'];

const DOCUMENT_FILE_SUB_ID = `[0-9a-f]{24}|${DOCUMENT_FILE_VARIANTS.join('|')}`;

// `/file/<type>/<objectId>[/<subId>]` at the very end of the url (a query
// string or fragment may follow).
const DOCUMENT_FILE_PATH_REGEX = new RegExp(
  `/file/(?:${DOCUMENT_FILE_TYPES.join(
    '|',
  )})/[0-9a-f]{24}(?:/(?:${DOCUMENT_FILE_SUB_ID}))?/?(?:[?#].*)?$`,
  'i',
);

/**
 * Whether a url is one of the authenticated `/file/*` document routes.
 * @param {string} url
 * @returns {boolean}
 */
const isDocumentFileUrl = url =>
  typeof url === 'string' && DOCUMENT_FILE_PATH_REGEX.test(url);

/**
 * Url to actually put in an `href` / `window.open` for a document link that
 * came from the API.
 *
 * @param {string} url The stored `link`/`url` of the document
 * @returns {string} a portal-relative `/file/...` path for the private-storage
 *   routes (so the browser sends the session cookie to THIS host), or `url`
 *   unchanged for legacy public and third-party urls
 */
const resolveDocumentUrl = url => {
  const match = isDocumentFileUrl(url)
    ? DOCUMENT_FILE_PATH_REGEX.exec(url)
    : null;
  if (!match) {
    return url;
  }
  return url.slice(match.index);
};

/**
 * Re-bases every /file/inbox-* url of a wall event onto this portal's
 * session-gated route (BRAVON-6332 Phase 3, Stage 3). One choke point for the
 * message renderers: images, the drive attachment and the uploaded video/pdf
 * source. Public urls and external links pass through untouched.
 *
 * @param message {Object} a wall event / inbox message
 * @returns {Object}
 */
const resolveInboxMediaUrls = message => {
  if (!message || typeof message !== 'object') {
    return message;
  }
  const resolveImage = img =>
    img && typeof img === 'object'
      ? {
          ...img,
          url: resolveDocumentUrl(img.url),
          medium: resolveDocumentUrl(img.medium),
          thumb: resolveDocumentUrl(img.thumb),
        }
      : img;
  return {
    ...message,
    image: resolveImage(message.image),
    icon: resolveImage(message.icon),
    file:
      message.file && typeof message.file === 'object'
        ? { ...message.file, url: resolveDocumentUrl(message.file.url) }
        : message.file,
    url: resolveDocumentUrl(message.url),
  };
};
/** endregion Sensitive documents */

/**
 * region Private video delivery (BRAVON-6332 Phase 2b, Stage 7)
 *
 * Two independent breakages, one region, because every player hits both.
 *
 * 1. `GET /api-media/media/course/:c/lesson/:l/link` answers a bare url STRING
 *    while `CLOUDFRONT_ENABLED` is off and an OBJECT
 *    (`{url, contentType, expiresAt, kind}`) once it is on — the node proxy
 *    unwraps api_media's `data` and forwards whichever it got.
 *    `resolveLessonMediaPayload` flattens both, so the flip is invisible to
 *    `LessonContent`. Mirrors `helpers/lessonMediaLink.ts` in `bravon-ui-kit`.
 *
 * 2. `url.endsWith('.m3u8')` stops being true. A tokened playlist is
 *    `….m3u8?vt=…` and a signed one `….m3u8?Policy=…`, so the suffix test
 *    silently drops HLS onto the plain-mp4 branch and paints a black
 *    `<video>`. `isHlsSource` reads the extension off the PATH and prefers the
 *    `contentType` the resolver reported.
 *
 * Session replays, chat videos and `User.video` additionally become
 * `/file/<type>/<id>` routes, which a `<video src>` cannot send a Bearer token
 * to — `resolveVideoUrl` re-bases those onto this portal exactly like a
 * document. Everything else is returned untouched, which is what keeps every
 * player byte-identical while the flags are off.
 */
const MEDIA_CONTENT_TYPES = {
  m3u8: 'application/vnd.apple.mpegurl',
  mpd: 'application/dash+xml',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  ogv: 'video/ogg',
};

const HLS_CONTENT_TYPES = [
  'application/vnd.apple.mpegurl',
  'application/x-mpegurl',
  'audio/mpegurl',
  'vnd.apple.mpegurl',
];

/**
 * Media type implied by a url's PATH, ignoring query string and fragment.
 *
 * @param {string} url
 * @returns {?string} null when the extension is unknown — "no information",
 *   not "not a video"
 */
const mediaContentTypeFromUrl = url => {
  if (typeof url !== 'string' || !url) return null;
  const extension = url.split(/[?#]/)[0].split('.').pop();
  return (extension && MEDIA_CONTENT_TYPES[extension.toLowerCase()]) || null;
};

/**
 * Whether a source must go through hls.js / native HLS.
 *
 * @param {string} url
 * @param {?string} [contentType] what the resolver reported, if anything
 * @returns {boolean}
 */
const isHlsSource = (url, contentType) => {
  if (typeof contentType === 'string' && contentType) {
    const normalized = contentType.toLowerCase();
    if (HLS_CONTENT_TYPES.some(type => normalized.includes(type))) {
      return true;
    }
  }
  return mediaContentTypeFromUrl(url) === MEDIA_CONTENT_TYPES.m3u8;
};

/**
 * Url to actually put in a `<video src>`. Shares ONE implementation with
 * document links so a chat video and the chat file next to it cannot drift.
 *
 * @param {string} url
 * @returns {string}
 */
const resolveVideoUrl = url => resolveDocumentUrl(url);

/**
 * Flattens whatever the lesson `/link` endpoint (or the node proxy in front of
 * it) answered.
 *
 * @param {*} payload
 * @param {number} [depth] recursion guard
 * @returns {?{url: string, contentType: ?string}} null when the shape is not
 *   recognised — callers keep whatever url they already had rather than
 *   rendering an empty player
 */
const resolveLessonMediaPayload = (payload, depth = 0) => {
  if (payload === null || payload === undefined || depth > 3) {
    return null;
  }
  if (typeof payload === 'string') {
    return payload.length ? { url: payload, contentType: null } : null;
  }
  if (typeof payload !== 'object') {
    return null;
  }

  const contentType =
    typeof payload.contentType === 'string' && payload.contentType.length
      ? payload.contentType
      : null;

  if (typeof payload.url === 'string') {
    return payload.url.length ? { url: payload.url, contentType } : null;
  }
  if (payload.url && typeof payload.url === 'object') {
    const nested = resolveLessonMediaPayload(payload.url, depth + 1);
    return nested
      ? { ...nested, contentType: nested.contentType || contentType }
      : null;
  }
  if ('data' in payload) {
    const nested = resolveLessonMediaPayload(payload.data, depth + 1);
    return nested
      ? { ...nested, contentType: nested.contentType || contentType }
      : null;
  }
  return null;
};
/** endregion Private video delivery */

/**
 * region Private video uploads (BRAVON-6332 Phase 2b, D10 option B)
 *
 * The families whose video is course content or personal data upload with a
 * `context.type`, which asks the API to put the object straight on the PRIVATE
 * bucket instead of the public one. That is a request, not a command: a server
 * with `PRIVATE_VIDEO_UPLOADS_ENABLED` off (or an older one) ignores it and
 * answers exactly what it always answered.
 *
 * When it DOES honour it the answer is
 * `{url: null, private: true, receipt, previewUrl, storage}`:
 *   - `receipt` is an opaque signed string and the only piece a client may
 *     send back. It rides inside the video object of the entity save, where
 *     the server verifies it and writes the pointer itself;
 *   - `storage` must NEVER be echoed (the server strips it);
 *   - `previewUrl` is a short-lived presigned GET for painting the thing
 *     before the save, never something to persist.
 *
 * Mirrors the same region in the admin portal's `utilityFunctions.js`.
 */
const VIDEO_UPLOAD_CONTEXT = {
  LESSON: 'lesson-video',
  SESSION_REPLAY: 'session-replay',
  USER: 'user-video',
};

/**
 * Narrows an upload response down to what may be persisted on the entity.
 *
 * @param {*} uploaded whatever the upload endpoint answered
 * @returns {*} `uploaded` untouched unless it reported a private upload, in
 *   which case a copy without `storage`/`private`/`previewUrl` (and with the
 *   receipt kept) is returned
 */
const toPersistableVideo = uploaded => {
  if (!uploaded || typeof uploaded !== 'object' || uploaded.private !== true) {
    return uploaded;
  }
  const {
    storage: _storage,
    private: _private,
    previewUrl: _previewUrl,
    ...rest
  } = uploaded;
  return rest;
};

/**
 * What to paint right after an upload, before the entity is saved: a private
 * object has no readable url of its own, only a short-lived presigned one.
 *
 * @param {*} uploaded whatever the upload endpoint answered
 * @returns {?string}
 */
const uploadPreviewUrl = uploaded => {
  if (!uploaded || typeof uploaded !== 'object') {
    return typeof uploaded === 'string' ? uploaded : null;
  }
  return uploaded.private === true
    ? uploaded.previewUrl || null
    : uploaded.url || null;
};
/** endregion Private video uploads */

export {
  SUBSCRIPTION_TYPE,
  SUBSCRIPTION_TYPES,
  PROFILE_BLOCK,
  PROFILE_BLOCKS,
  PROFILE_TAB,
  PROFILE_TABS,
  CHALLENGE_TAB,
  CHALLENGE_TABS,
  INBOX_MSG_TYPE,
  INBOX_MSG_TYPES,
  INBOX_FILTER,
  INBOX_FILTERS,
  TROPHY_CONFIG,
  GAME_TYPE,
  GAME_TYPES,
  GAME_STATUS,
  GAME_STATUSES,
  GAME_EVENT_TYPE,
  GAME_EVENT_TYPES,
  LEAD_CONDITION,
  LEAD_CONDITIONS,
  LEAD_STATUS,
  LEAD_STATUSES,
  LEAD_DETAILS_TAB,
  LEAD_DETAILS_TABS,
  COURSE_STATUS,
  COURSE_STATUSES,
  isEnrolled,
  isEnrolledOrFree,
  isItemFree,
  isJourneyUnlocked,
  isAwaitingPaymentConfirmation,
  VOUCHER_STATUS,
  VOUCHER_STATUSES,
  COURSE_PROVIDER,
  COURSE_PROVIDERS,
  MODULE_TYPE,
  MODULE_TYPES,
  MODULE_STATUS,
  MODULE_STATUSES,
  LESSON_TYPE,
  LESSON_TYPES,
  LESSON_CONTENT_TYPE,
  LESSON_CONTENT_TYPES,
  LESSON_STATUS,
  LESSON_STATUSES,
  QUESTION_STATUS,
  QUESTION_STATUSES,
  JOURNEY_STATUS,
  JOURNEY_STATUSES,
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_STATUSES,
  JOURNEY_TASK_STATUS,
  JOURNEY_TASK_STATUSES,
  JOURNEY_STEP_TYPE,
  JOURNEY_STEP_TYPES,
  normalizeJourney,
  SURVEY_TYPE,
  SURVEY_TYPES,
  SURVEY_QUESTION_TYPE,
  SURVEY_QUESTION_TYPES,
  WORK_TIME,
  WORK_TIMES,
  VEHICLE,
  VEHICLES,
  IN_APP_PRODUCT_TYPE,
  IN_APP_PRODUCT_TYPES,
  TOPIC_CHAPTER,
  TOPIC_CHAPTERS,
  TOPIC_TYPE,
  TOPIC_TYPES,
  SOCIAL_MEDIA_CONFIG,
  SOCIAL_MEDIA_SITE,
  SOCIAL_MEDIA_SITES,
  ACADEMY_LIST_ITEM_TYPE,
  ACADEMY_LIST_ITEM_TYPES,
  USER_DOC_TYPE,
  USER_DOC_TYPES,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUSES,
  PLAN_STATUS,
  PLAN_STATUSES,
  ORDER_STATUSES,
  ORDER_HISTORY_TYPES,
  BUYER_TYPE,
  BUYER_TYPES,
  PRODUCT_TYPE,
  PRODUCT_TYPES,
  SESSION_STATUS,
  SESSION_STATUSES,
  SESSION_TYPE,
  SESSION_TYPES,
  SESSION_MODE,
  ORDER_STATUS_ENUM,
  ACADEMY_ORDER_STATUSES,
  isPaymentPending,
  returnAcademyContext,
  displayNumber,
  displayMoney,
  displayUserName,
  displayRegionalAddress,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
  displayTrophyName,
  snakeToCamel,
  debounce,
  throttle,
  compose,
  colors,
  changeLightness,
  displayCardNumber,
  addAlphaToHex,
  hexToHSL,
  hslToHex,
  handleImageUpload,
  partitionArray,
  getSizeFromString,
  getWidthForElement,
  formatDate,
  formatDateTime,
  dateWithoutTimeZone,
  uuid,
  linkify,
  formatPrice,
  showProgress,
  getLink,
  findCurrencySymbol,
  handleStripePayment,
  formatNumbers,
  formatNumbersWithZero,
  newFormatPrice,
  getSeparator,
  formatURL,
  encodeString,
  decodeString,
  parseCookies,
  returnParams,
  gameStatus,
  getBrowserFullscreenElementProp,
  handleClickFullScreen,
  returnAddress,
  checkPromoDate,
  calculatePromoPercentage,
  PROMO_ERRORS,
  PAYMENT_METHODS,
  PAYMENT_METHOD,
  handleUploadFile,
  RewardTypes,
  checkStripeKeys,
  getDifferenceBetweenDates,
  promotionEndingMessage,
  renderPromotionMessage,
  FrequencySubscriptionTypes,
  FREQUENCIES_TYPES,
  STEPS_CONFIG,
  composeDate,
  downloadFile,
  extractLanguageFromCookies,
  handleInitTab,
  isBusinessTypeESAMAllowed,
  formatVA,
  DOCUMENT_FILE_TYPE,
  DOCUMENT_FILE_TYPES,
  isDocumentFileUrl,
  resolveDocumentUrl,
  resolveInboxMediaUrls,
  mediaContentTypeFromUrl,
  isHlsSource,
  resolveVideoUrl,
  resolveLessonMediaPayload,
  VIDEO_UPLOAD_CONTEXT,
  toPersistableVideo,
  uploadPreviewUrl,
};
